import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';  // Add this import for reliable file saving

export const downloadQRCodesAsPrintable = async (qrCodes, filename, format, fgColor = "#000000", bgColor = "#FFFFFF", logo = null, logoSize = 30) => {
  try {
    console.log('Starting PDF generation with format:', format);
    
    if (!format || !format.pageSize || !format.labels) {
      throw new Error('Invalid format specification');
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [format.pageSize.width, format.pageSize.height]  // Use width and height explicitly
    });

    // Get label specifications from the format
    const { labels } = format;
    const { columns, rows, width, height, marginTop, marginLeft, columnGap, rowGap } = labels;
    const labelsPerPage = columns * rows;
    
    console.log('Label specifications:', { columns, rows, width, height, marginTop, marginLeft, columnGap, rowGap });

    for (let i = 0; i < qrCodes.length; i++) {
      if (i > 0 && i % labelsPerPage === 0) {
        doc.addPage();
      }

      const qr = qrCodes[i];
      const content = typeof qr === 'string' ? qr : qr.content; // Handle both string and object formats
      
      if (!content) {
        console.warn(`Skipping QR code ${i + 1} due to missing content`);
        continue;
      }

      const pageIndex = i % labelsPerPage;
      const col = pageIndex % columns;
      const row = Math.floor(pageIndex / columns);

      // Create QR code
      const canvas = document.createElement('canvas');
      canvas.width = 300;
      canvas.height = 300;

      await QRCode.toCanvas(canvas, content, {
        width: 300,
        margin: 1,
        color: {
          dark: fgColor,
          light: bgColor
        },
        errorCorrectionLevel: 'M'
      });

      // If logo is provided, add it to the canvas
      if (logo) {
        const ctx = canvas.getContext('2d');
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = logo;
        });

        const logoWidth = Math.floor(300 * (logoSize / 100));
        const logoHeight = logoWidth;
        const x = (300 - logoWidth) / 2;
        const y = (300 - logoHeight) / 2;

        // Draw white background for logo
        ctx.fillStyle = bgColor;
        ctx.fillRect(x, y, logoWidth, logoHeight);
        
        // Draw logo
        ctx.drawImage(img, x, y, logoWidth, logoHeight);
      }

      // Convert QR code to base64
      const pngData = canvas.toDataURL("image/png");

      // Calculate position using Avery specifications
      const x = marginLeft + (col * (width + columnGap));
      const y = marginTop + (row * (height + rowGap));

      // Calculate QR code size to fit within label while maintaining aspect ratio
      const qrSize = Math.min(width, height) * 0.9; // 90% of the smallest dimension
      const qrX = x + (width - qrSize) / 2;
      const qrY = y + (height - qrSize) / 2;

      console.log(`Adding QR code ${i + 1} at position:`, { x: qrX, y: qrY, size: qrSize });

      try {
        // Add QR code to PDF
        doc.addImage(pngData, 'PNG', qrX, qrY, qrSize, qrSize);

        // Add label below QR code if there's space
        if (height - qrSize > 5) { // Only add label if there's at least 5mm space
          doc.setFontSize(6);
          doc.text(`QR Code ${i + 1}`, x + (width / 2), y + height - 1, { align: 'center' });
        }
      } catch (imageError) {
        console.error(`Error adding QR code ${i + 1} to PDF:`, imageError);
      }
    }

    console.log('PDF generation completed, attempting to save...');

    // Generate blob and use saveAs instead of doc.save
    const pdfBlob = doc.output('blob');
    saveAs(pdfBlob, filename);

    console.log('PDF saved successfully');
    return true;
  } catch (error) {
    console.error('Detailed error in PDF generation:', error);
    // Log the full error details
    if (error.stack) {
      console.error('Error stack:', error.stack);
    }
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
}; 